import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePortalLocation } from '../contexts/PortalLocationContext';
import { useTheme } from '../contexts/ThemeContext';
import { getMyMachines, getMyServiceCalls } from '../lib/api';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  LayoutDashboard,
  Calculator,
  CreditCard,
  Wrench,
  FileText,
  LogOut,
  Menu,
  X,
  MapPin,
  Gamepad2,
  Users,
  Settings,
  Monitor,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Building,
  DollarSign,
  Store,
  Handshake
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Sun, Moon } from 'lucide-react';


const LocationPortalLayout = () => {
  const { user, logout } = useAuth();
  const { locations, selectedLocation, selectLocation, loading: locationsLoading } = usePortalLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [machines, setMachines] = useState([]);
  const [serviceCalls, setServiceCalls] = useState([]);
  const [machinesExpanded, setMachinesExpanded] = useState(false);
  const [serviceCallsExpanded, setServiceCallsExpanded] = useState(false);

  const isEmployee = user?.role === 'location_employee';

  useEffect(() => {
    if (selectedLocation) {
      loadLocationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  const loadLocationData = async () => {
    if (!selectedLocation) return;
    try {
      const [machRes, callsRes] = await Promise.all([
        getMyMachines(selectedLocation.id),
        (isEmployee || !selectedLocation.company_id) ? Promise.resolve({ data: [] }) : getMyServiceCalls(null, selectedLocation.id)
      ]);
      setMachines(machRes.data);
      setServiceCalls(callsRes.data?.filter(c => c.status !== 'solved') || []);
    } catch (error) {
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleLocationChange = (locationId) => {
    const loc = locations.find(l => l.id === locationId);
    if (loc) {
      selectLocation(loc);
    }
  };

  const isIndependent = user?.is_independent_location;
  const hasCompany = !!selectedLocation?.company_id;
  const isPartner = user?.role === 'partner';
  const isAdmin = user?.role === 'location_user';

  // Navigation items based on role
  const navItems = isEmployee ? [
    { to: '/location-portal', icon: LayoutDashboard, label: 'Dashboard', end: true },
    { to: '/location-portal/bookkeeping', icon: Calculator, label: 'Machine Accounting' },
  ] : (isIndependent || isPartner) ? [
    { to: '/location-portal', icon: LayoutDashboard, label: 'Dashboard', end: true },
    { to: '/location-portal/all-locations', icon: LayoutDashboard, label: 'All Locations', showMultiOnly: true },
    ...(isAdmin ? [{ to: '/location-portal/my-locations', icon: Building, label: 'My Locations' }] : []),
    { to: '/location-portal/my-machines', icon: Monitor, label: 'Machines' },
    ...(isAdmin ? [{ to: '/location-portal/vendors', icon: Store, label: 'Vendors' }] : []),
    ...(isAdmin ? [{ to: '/location-portal/partners', icon: Handshake, label: 'Partners' }] : []),
    { to: '/location-portal/bookkeeping', icon: Calculator, label: 'Bookkeeping' },
    ...(isAdmin ? [{ to: '/location-portal/employees', icon: Users, label: 'Employees' }] : []),
    ...(hasCompany ? [{ to: '/location-portal/service-calls', icon: Wrench, label: 'Service Calls' }] : []),
    { to: '/location-portal/reports', icon: FileText, label: 'Reports' },
    ...(isAdmin ? [{ to: '/location-portal/user-management', icon: Users, label: 'User Management', testId: 'portal-nav-user-mgmt' }] : []),
  ] : [
    { to: '/location-portal', icon: LayoutDashboard, label: 'Dashboard', end: true },
    { to: '/location-portal/all-locations', icon: LayoutDashboard, label: 'All Locations', showMultiOnly: true },
    { to: '/location-portal/bookkeeping', icon: Calculator, label: 'Machine Accounting' },
    { to: '/location-portal/credits', icon: CreditCard, label: 'Credits' },
    ...(hasCompany ? [{ to: '/location-portal/service-calls', icon: Wrench, label: 'Service Calls' }] : []),
    { to: '/location-portal/reports', icon: FileText, label: 'Reports' },
    { to: '/location-portal/employees', icon: Users, label: 'Employees' },
  ];

  const activeServiceCallsCount = serviceCalls.length;

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-card border-b border-border z-50 flex items-center justify-between px-4">
        <button 
          onClick={() => setSidebarOpen(!sidebarOpen)} 
          className="p-2 rounded-lg hover:bg-secondary"
          data-testid="portal-mobile-menu"
        >
          {sidebarOpen ? <X className="w-6 h-6 text-foreground" /> : <Menu className="w-6 h-6 text-foreground" />}
        </button>
        <div className="flex items-center gap-2">
          <Gamepad2 className="w-6 h-6 text-amber-500" />
          <span className="font-bold text-amber-500">Location Portal</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-secondary"
            data-testid="portal-mobile-theme-toggle"
          >
            {isDarkMode ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-muted-foreground" />}
          </button>
          <button 
            onClick={handleLogout}
            className="p-2 rounded-lg hover:bg-secondary"
            data-testid="portal-mobile-logout"
            aria-label="Sign out"
          >
            <LogOut className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>
      </header>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - matches main dashboard theme */}
      <aside
        className={`fixed top-14 lg:top-0 left-0 z-50 h-[calc(100vh-3.5rem)] lg:h-screen w-72 bg-card border-r border-border transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Logo - Desktop only */}
          <div className="hidden lg:flex h-14 items-center gap-3 px-4 border-b border-border">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
              <Gamepad2 className="w-5 h-5 text-black" />
            </div>
            <span className="font-bold text-xl text-amber-500">Location Portal</span>
          </div>

          {/* Location Selector */}
          <div className="px-4 py-4 border-b border-border">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
              <Building className="w-4 h-4" />
              <span>Select Location</span>
            </div>
            {locationsLoading ? (
              <p className="text-muted-foreground text-sm">Loading locations...</p>
            ) : locations.length > 1 ? (
              <Select 
                value={selectedLocation?.id || ''} 
                onValueChange={handleLocationChange}
              >
                <SelectTrigger className="bg-secondary border-border text-foreground hover:bg-secondary/80">
                  <SelectValue placeholder="Select location">
                    {selectedLocation?.name || 'Select location'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {locations.map(loc => (
                    <SelectItem key={loc.id} value={loc.id}>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3 h-3" />
                        <span>{loc.name}</span>
                        <Badge variant="outline" className="ml-2 text-xs">{loc.machine_count || 0}</Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-foreground font-semibold truncate">
                {selectedLocation?.name || 'No location assigned'}
              </p>
            )}
            {selectedLocation?.address && (
              <p className="text-muted-foreground text-xs truncate mt-1">{selectedLocation.address}</p>
            )}
            {isEmployee && user?.assigned_shift && (
              <Badge className="mt-2 bg-amber-500/20 text-amber-500 text-xs">
                Assigned: {user.assigned_shift.replace('_', ' ').toUpperCase()}
              </Badge>
            )}
          </div>

          {/* Navigation - Scrollable */}
          <div className="flex-1 overflow-y-auto py-4 px-2">
            <nav>
              {navItems.filter(item => !item.showMultiOnly || locations.length > 1).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                      isActive
                        ? 'bg-amber-500 text-black'
                        : 'text-muted-foreground hover:bg-secondary hover:text-amber-500'
                    }`
                  }
                  data-testid={`portal-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </NavLink>
              ))}
            </nav>

            {/* Machines Section (Collapsible) */}
            <div className={`mt-4 border-t border-border pt-4`}>
              <button
                onClick={() => setMachinesExpanded(!machinesExpanded)}
                className={`flex items-center justify-between w-full px-4 py-2 text-muted-foreground hover:text-foreground transition-colors`}
              >
                <div className="flex items-center gap-2">
                  <Monitor className="w-4 h-4" />
                  <span className="text-sm font-medium">Machines ({machines.length})</span>
                </div>
                {machinesExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
              {machinesExpanded && (
                <div className="mt-2 max-h-40 overflow-y-auto">
                  {machines.slice(0, 10).map((machine) => (
                    <div key={machine.id} className={`px-6 py-1.5 text-muted-foreground text-xs`}>
                      <p className="text-foreground font-medium">{machine.machine_id}</p>
                      <p className="truncate">{machine.game_name}</p>
                    </div>
                  ))}
                  {machines.length > 10 && (
                    <p className={`px-6 py-1 text-xs text-muted-foreground`}>+{machines.length - 10} more</p>
                  )}
                </div>
              )}
            </div>

            {/* Service Calls Section (Collapsible) - Only for location_user with company */}
            {!isEmployee && hasCompany && (
              <div className={`mt-2 border-t border-border pt-4`}>
                <button
                  onClick={() => setServiceCallsExpanded(!serviceCallsExpanded)}
                  className={`flex items-center justify-between w-full px-4 py-2 text-muted-foreground hover:text-foreground transition-colors`}
                >
                  <div className="flex items-center gap-2">
                    <Wrench className="w-4 h-4" />
                    <span className="text-sm font-medium">Active Calls</span>
                    {activeServiceCallsCount > 0 && (
                      <Badge className="bg-yellow-500 text-foreground text-xs px-1.5 py-0">{activeServiceCallsCount}</Badge>
                    )}
                  </div>
                  {serviceCallsExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                {serviceCallsExpanded && (
                  <div className="mt-2 max-h-40 overflow-y-auto">
                    {serviceCalls.length === 0 ? (
                      <p className={`px-6 py-2 text-xs text-muted-foreground`}>No active calls</p>
                    ) : (
                      serviceCalls.slice(0, 5).map((call) => (
                        <div key={call.id} className={`px-6 py-1.5 text-muted-foreground text-xs border-l-2 border-yellow-400 ml-4`}>
                          <p className="text-foreground font-medium truncate">{call.title}</p>
                          <p className="capitalize">{call.status.replace('_', ' ')}</p>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User Section */}
          <div className="border-t border-border">
            <div className="p-4">
              <p className={`text-muted-foreground text-sm mb-2 truncate`}>{user?.name || user?.email}</p>
              {isEmployee && (
                <p className={`text-muted-foreground text-xs mb-2`}>Employee</p>
              )}
              <div className="flex gap-2 mb-2">
                <Button
                  onClick={toggleTheme}
                  variant="outline"
                  size="sm"
                  className="flex-1 bg-transparent border-border text-muted-foreground hover:bg-secondary hover:text-foreground"
                  data-testid="theme-toggle-btn"
                >
                  {isDarkMode ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                  {isDarkMode ? 'Light' : 'Dark'}
                </Button>
              </div>
              <Button
                onClick={handleLogout}
                variant="outline"
                className={`w-full bg-transparent border-border text-muted-foreground hover:bg-secondary hover:text-foreground`}
                data-testid="portal-logout"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-72 pt-14 lg:pt-0 min-h-screen">
        <div className="p-4 lg:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default LocationPortalLayout;
